/**
 * FOLLOWER BOOST — V6 scene engine (fiches prestations + galerie de couverture).
 * DOM + CSS animations only (no canvas loop). One IntersectionObserver for every scene:
 *  - .is-live while visible (animations run, video plays), paused off-screen / tab hidden;
 *  - native counters [data-fbs-n] go from their illustrative base to base + pack quantity;
 *  - pack change (fb:pack on [data-fb-config], or fbs:qty on the scene) → recount + bump;
 *  - prefers-reduced-motion → final static state, no video, no loop;
 *  - pause / play button (keyboard) for anything that moves longer than 5 s.
 * Counters are illustrative and follow the selected pack: never a promise of results.
 */
const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
const lang = (document.documentElement.lang || 'fr').slice(0, 2);
const fFull = new Intl.NumberFormat(lang);
const fComp = new Intl.NumberFormat(lang, { notation: 'compact', maximumFractionDigits: 1 });
const fmt = (n, f) => (f === 'c' && n >= 10000 ? fComp.format(n) : fFull.format(n));
const toInt = (v) => {
  const n = parseInt(String(v ?? '').replace(/[^\d]/g, ''), 10);
  return Number.isFinite(n) ? n : null;
};

function setText(el, v) {
  el.textContent = fmt(v, el.dataset.f);
  el._v = v;
}

function count(el, to, delay = 0) {
  cancelAnimationFrame(el._raf);
  clearTimeout(el._to);
  const from = el._v ?? toInt(el.dataset.fbsN) ?? 0;
  if (reduce.matches || from === to) return setText(el, to);
  const run = () => {
    const t0 = performance.now();
    const dur = 1500;
    const step = (t) => {
      const k = Math.min(1, (t - t0) / dur);
      setText(el, Math.round(from + (to - from) * (1 - Math.pow(1 - k, 3))));
      if (k < 1) el._raf = requestAnimationFrame(step);
    };
    el._raf = requestAnimationFrame(step);
  };
  el._to = setTimeout(run, delay);
}

function apply(scene, animate) {
  const q = scene._q;
  scene.querySelectorAll('[data-fbs-q]').forEach((el) => (el.textContent = '+' + fFull.format(q)));
  scene.querySelectorAll('[data-fbs-n]').forEach((el, i) => {
    const base = toInt(el.dataset.fbsN) ?? 0;
    const k = parseFloat(el.dataset.k || '1');
    const to = base + Math.round(q * k);
    if (!animate) {
      if (el._v == null) el._v = base;
      return;
    }
    count(el, to, i * 120);
  });
}

function setLive(scene, on) {
  scene.classList.toggle('is-live', on);
  scene.querySelectorAll('video[data-src]').forEach((v) => {
    if (on && !scene.classList.contains('is-paused') && !scene.classList.contains('is-off')) {
      if (!v.src) {
        v.src = v.dataset.src;
        v.addEventListener('playing', () => v.classList.add('is-on'), { once: true });
      }
      v.play().catch(() => {});
    } else if (v.src) v.pause();
  });
}

function setQty(scene, q) {
  if (!q || q === scene._q) return;
  scene._q = q;
  apply(scene, true);
  scene.classList.remove('is-bump');
  void scene.offsetWidth;
  scene.classList.add('is-bump');
}

const io =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) =>
          entries.forEach((en) => {
            const s = en.target;
            const vis = en.isIntersecting && !document.hidden;
            if (vis && !s._seen) {
              s._seen = true;
              apply(s, true);
            }
            if (s.classList.contains('is-anim')) setLive(s, vis);
          }),
        { threshold: 0.2 }
      )
    : null;

function bind(scene) {
  if (scene.dataset.fbsOk) return;
  scene.dataset.fbsOk = '1';
  scene._q = toInt(scene.dataset.qty) || 1000;

  // media: loading skeleton until the picture is decoded
  scene.querySelectorAll('.fbs-m img').forEach((img) => {
    const done = () => img.closest('.fbs-m')?.classList.add('is-ld');
    if (img.complete) done();
    else {
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    }
  });

  // static start: show the base values, then count when on screen
  scene.querySelectorAll('[data-fbs-n]').forEach((el) => setText(el, toInt(el.dataset.fbsN) ?? 0));
  apply(scene, false);

  const motion = !reduce.matches && !scene.classList.contains('is-off');
  if (motion) scene.classList.add('is-anim');
  const btn = scene.querySelector('[data-fbs-toggle]');
  if (btn && motion) {
    btn.hidden = false;
    btn.addEventListener('click', () => {
      const paused = !scene.classList.contains('is-paused');
      scene.classList.toggle('is-paused', paused);
      btn.setAttribute('aria-pressed', String(paused));
      btn.setAttribute('aria-label', paused ? btn.dataset.lPlay : btn.dataset.lPause);
      setLive(scene, scene.classList.contains('is-live'));
    });
  }

  if (io) io.observe(scene);
  else {
    apply(scene, true);
    if (motion) setLive(scene, true);
  }

  scene.addEventListener('fbs:qty', (e) => setQty(scene, toInt(e.detail)));
  const config = scene.closest('[data-fb-config]');
  config?.addEventListener('fb:pack', () => {
    const r = config.querySelector('[data-fb-packs] input[type="radio"]:checked');
    setQty(scene, toInt(r?.dataset.quantity) ?? toInt(r?.dataset.title));
  });
}

function bindGallery(root) {
  root.querySelectorAll('[data-fbs-set]:not([data-fbs-ok])').forEach((b) => {
    b.dataset.fbsOk = '1';
    b.addEventListener('click', () => {
      const card = b.closest('[data-fbs-card]');
      card?.querySelectorAll('[data-fbs-set]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      card?.querySelector('[data-fbs]')?.dispatchEvent(new CustomEvent('fbs:qty', { detail: b.dataset.fbsSet }));
      const m = card?.querySelector('[data-fbs-metric]');
      if (m) m.textContent = '+' + fFull.format(toInt(b.dataset.fbsSet));
    });
  });
}

function init(root = document) {
  root.querySelectorAll('[data-fbs]').forEach(bind);
  bindGallery(root);
}

document.addEventListener('visibilitychange', () => {
  document.querySelectorAll('[data-fbs].is-anim').forEach((s) => {
    if (document.hidden) return setLive(s, false);
    const r = s.getBoundingClientRect();
    setLive(s, r.bottom > 0 && r.top < window.innerHeight);
  });
});
init();
document.addEventListener('shopify:section:load', (e) => init(e.target));
